import { useCallback } from 'react';
import { Download, Eye, ThreeDotsVertical } from 'react-bootstrap-icons';

import { Button, Dropdown, Space, Typography } from 'antd';
import type { MenuProps } from 'antd';

import BaseCard from '@/components/shared/BaseCard';
import { aspectRatioPadding } from '@/components/shared/ImageRatio';
import { getCdnUrl } from '@/helpers';
import CertificateProps from '@/interfaces/certificates';
import styled, { StyledProp, css } from '@/styled';

interface CertificateCardProps extends StyledProp, CertificateProps {}

const CertificateCard = ({ hash, title, course_image: image, url, className }: CertificateCardProps) => {
  const handleClickView = useCallback(() => window.open(url, '_blank'), [url]);

  const handleClickDownload = useCallback(() => window.open(`${url}?download=true`, '_blank'), [url]);

  const items: MenuProps['items'] = [
    {
      key: 'view',
      icon: <Eye />,
      label: 'Visualizar',
      onClick: handleClickView
    },
    {
      key: 'download',
      icon: <Download />,
      label: 'Baixar',
      onClick: handleClickDownload
    }
  ];

  return (
    <BaseCard
      className={className}
      internalSpace={16}
      image={{ src: getCdnUrl(image), aspect: '16:9', alt: title }}
      extraContent={
        <div className='certificate-card-actions'>
          <Space className='certificate-card-actionsContent' direction='vertical' size={16}>
            <Button icon={<Download />} block onClick={handleClickDownload} id='btn-download-certificate'>
              Baixar
            </Button>

            <Button icon={<Eye />} block onClick={handleClickView} id='btn-see-certificate'>
              Visualizar
            </Button>
          </Space>
        </div>
      }
      id={`certificates-card-${hash}`}
    >
      <div className='certificate-card-data'>
        <Typography.Text strong ellipsis>
          {title}
        </Typography.Text>

        <div className='certificate-card-data-mobileActions'>
          <Dropdown menu={{ items }} trigger={['click']} placement='bottomRight'>
            <Button size='small' type='ghost' shape='circle'>
              <ThreeDotsVertical />
            </Button>
          </Dropdown>
        </div>
      </div>
    </BaseCard>
  );
};

export default styled(CertificateCard)`
  ${({ theme }) => {
    return css`
      ${theme.breakpoints.up('lg')} {
        &:hover {
          .certificate-card-actions {
            opacity: 1;
            pointer-events: all;
          }
        }
      }

      .certificate-card-data {
        display: flex;

        ${theme.breakpoints.down('lg')} {
          justify-content: space-between;
        }

        .certificate-card-data-mobileActions {
          display: none;

          ${theme.breakpoints.down('lg')} {
            display: flex;
          }
        }
      }

      .certificate-card-actions {
        transition: ${theme.motionDurationSlow};
        opacity: 0;
        pointer-events: none;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        z-index: 2;
        background-color: ${theme.palette.alpha('#000000', 0.9)};
        height: 0;
        padding-top: ${aspectRatioPadding['16:9']};
        overflow: hidden;
        border-radius: ${theme.borderRadius}px;

        .certificate-card-actionsContent {
          display: flex;
          flex-direction: column;
          padding: ${theme.space.spacing(4, 12)};
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .ant-space-item {
          width: 100%;
        }

        button,
        a {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          height: 40px;
          background: ${theme.palette.gray[9]};
          border-color: ${theme.palette.gray[7]};
          color: ${theme.palette.gray[0]};
        }
      }
    `;
  }}
`;
